import { useRef } from 'react';

import { graphql, fetchQuery, useRelayEnvironment } from 'react-relay';

import { createAtlAttributionHeader } from '../../utils';
import { GQLExtensionContextsFilterType } from '../graphql/types';
import type { useEmitPublicEvent_getExtensionListQuery } from './__generated__/useEmitPublicEvent_getExtensionListQuery.graphql';

type Extension = {
	appId?: string;
	key?: string;
	properties?: any;
};

const CORE_EVENT = 'core:event';
const CLIENT_EVENTS = 'client-events';
const CLIENT_EVENT = 'client-event';

const EVENT_PREFIX = 'forge.bridge.';

type ExtensionDetails = {
	appId: string | undefined;
	id: string;
	installationId?: string;
};

export type EmitPublicEvent = (data: { event: string; payload?: any }) => Promise<void>;

const filterAllowedRecipients = (allExtensions: Extension[], extension: ExtensionDetails) => {
	const matchingExtension = allExtensions.find((ext: Extension) => {
		if (ext && ext?.appId && ext?.key) {
			return (
				ext.appId === extension.appId && (ext.key === CLIENT_EVENTS || ext.key === CLIENT_EVENT)
			);
		}
	});
	const allowedRecipients = matchingExtension?.properties?.allowedRecipients ?? [];

	const filteredAllowedRecipients = allowedRecipients
		.filter((recipient: string) => recipient !== '*')
		.map((recipient: string) => recipient.split('/').pop());

	if (!filteredAllowedRecipients.includes(extension.appId)) {
		filteredAllowedRecipients.push(extension.appId);
	}

	return filteredAllowedRecipients;
};

export const useEmitPublicEvent = (
	contextId: string,
	extension: ExtensionDetails,
): EmitPublicEvent => {
	const relayEnvironment = useRelayEnvironment();
	const allowedRecipientsRef = useRef<(string | undefined)[] | null>(null);

	if (!extension?.appId) {
		return async () => {};
	}

	const getAllowedRecipients = async () => {
		if (allowedRecipientsRef.current !== null) {
			return allowedRecipientsRef.current;
		}

		if (relayEnvironment) {
			const observable = fetchQuery<useEmitPublicEvent_getExtensionListQuery>(
				relayEnvironment,
				graphql`
					query useEmitPublicEvent_getExtensionListQuery(
						$contextIds: [ID!]!
						$filter: [ExtensionContextsFilter!]!
					) {
						extensionContexts(contextIds: $contextIds) {
							extensions(filter: $filter) {
								appId
								properties
								key
							}
						}
					}
				`,
				{
					contextIds: [contextId],
					filter: [
						{
							type: GQLExtensionContextsFilterType.ExtensionType,
							value: [CORE_EVENT],
						},
					],
				},
				{
					networkCacheConfig: {
						metadata: {
							headers: {
								'atl-attribution': createAtlAttributionHeader(),
							},
						},
					},
				},
			);

			return new Promise<string[]>((resolve) => {
				observable.subscribe({
					next: (result) => {
						const allExtensions = result?.extensionContexts?.[0]?.extensions || [];
						const filteredAllowedRecipients = filterAllowedRecipients(
							Array.from(allExtensions),
							extension,
						);
						resolve(filteredAllowedRecipients);
					},
					error: () => {
						resolve([extension.appId ?? '']);
					},
				});
			});
		}

		return [extension.appId];
	};

	return async (eventData: { event: string; payload?: any }) => {
		const { event, payload } = eventData;

		if (allowedRecipientsRef.current === null) {
			const allowedRecipients = await getAllowedRecipients();
			allowedRecipientsRef.current = allowedRecipients;
		}

		const newEvent = new CustomEvent(`${EVENT_PREFIX}${event}`, {
			detail: {
				extension,
				payload,
				allowedRecipients: allowedRecipientsRef.current ?? [extension.appId],
			},
		});

		window.document.dispatchEvent(newEvent);
	};
};
